import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  hubResourcesApi,
  HubResource,
  CreateHubResourceRequest,
  UpdateHubResourceRequest,
  ListParams,
  RESOURCE_TYPES,
  MEETING_RECORDING_TYPE,
  FILE_TYPE_FILTERS,
  ALLOWED_EXTENSIONS,
  validateFileForUpload,
} from '../../api/hubResources';
import { useAuth } from '../../auth/AuthContext';

const STALE_BANNER_MS = 5000;
const SEARCH_DEBOUNCE_MS = 300;
const SHOW_EXPIRED_KEY = 'brg.showExpiredRecordings';

// ─── Inject scrollbar styling ──────────────────────────────────────────────
const injectScrollbarStyles = () => {
  const styleId = 'resource-hub-scrollbar-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .scrollable-grid {
        scrollbar-width: auto;
        scrollbar-color: #999 #f0f0f0;
      }
      .scrollable-grid::-webkit-scrollbar {
        width: 12px;
      }
      .scrollable-grid::-webkit-scrollbar-track {
        background: #f0f0f0;
        border-radius: 6px;
      }
      .scrollable-grid::-webkit-scrollbar-thumb {
        background: #999;
        border-radius: 6px;
        border: 2px solid #f0f0f0;
      }
      .scrollable-grid::-webkit-scrollbar-thumb:hover {
        background: #666;
      }
    `;
    document.head.appendChild(style);
  }
};

interface BannerState {
  text: string;
  visible: boolean;
}

export function ResourceHubPage() {
  const auth = useAuth();
  const isAdmin = auth.roles?.includes('BRG.Admin') ?? false;

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [fileType, setFileType] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | number | 'meetings'>('all');
  const [showExpired, setShowExpired] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SHOW_EXPIRED_KEY) === 'true';
  });

  // Banner + modal state
  const [banner, setBanner] = useState<BannerState>({ text: '', visible: false });
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<HubResource | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<HubResource | null>(null);

  // Inject scrollbar styles on mount
  useEffect(() => {
    injectScrollbarStyles();
  }, []);

  // Debounce search input → search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Persist show-expired toggle
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SHOW_EXPIRED_KEY, String(showExpired));
    }
  }, [showExpired]);

  // Banner auto-dismiss
  useEffect(() => {
    if (banner.visible) {
      const t = setTimeout(() => setBanner((b) => ({ ...b, visible: false })), STALE_BANNER_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [banner.visible]);

  const listParams: ListParams = useMemo(
    () => ({
      search: search || undefined,
      fileType: fileType || undefined,
      showExpired: isAdmin && showExpired,
    }),
    [search, fileType, showExpired, isAdmin],
  );

  const listQuery = useQuery({
    queryKey: ['hub-resources', listParams],
    queryFn: () => hubResourcesApi.list(listParams),
  });

  const showBanner = (text: string) => setBanner({ text, visible: true });

  const visibleResources = listQuery.data?.items ?? [];

  // Group resources by resourceType for tab/section rendering
  const groupedByType = useMemo(() => {
    const groups = new Map<number, HubResource[]>();
    for (const r of visibleResources) {
      const arr = groups.get(r.resourceType) ?? [];
      arr.push(r);
      groups.set(r.resourceType, arr);
    }
    return groups;
  }, [visibleResources]);

  const meetingRecordings = groupedByType.get(MEETING_RECORDING_TYPE) ?? [];

  const sectionVisible = (sectionKey: 'meetings' | number) =>
    activeTab === 'all' || activeTab === sectionKey;

  const totalForActiveTab =
    activeTab === 'all'
      ? visibleResources.length
      : activeTab === 'meetings'
        ? meetingRecordings.length
        : (groupedByType.get(activeTab as number)?.length ?? 0);

  return (
    <div style={{ width: '100%', padding: '20px 80px', maxWidth: 'none' }}>
      <ConfirmBanner banner={banner} />

      {/* Top controls */}
      <div style={topControlsStyle}>
        <input
          type="text"
          placeholder="Search resources..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={searchInputStyle}
        />
        <select value={fileType} onChange={(e) => setFileType(e.target.value)} style={filterSelectStyle}>
          {FILE_TYPE_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        {isAdmin && (
          <button onClick={() => setAddOpen(true)} style={primaryBtnStyle}>
            + Add New BRG Resource
          </button>
        )}
      </div>

      {/* Category tabs */}
      <div style={tabsStyle}>
        <Tab active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
          All
        </Tab>
        <Tab active={activeTab === 'meetings'} onClick={() => setActiveTab('meetings')}>
          Meeting Recordings
        </Tab>
        {RESOURCE_TYPES.map((rt) => (
          <Tab key={rt.value} active={activeTab === rt.value} onClick={() => setActiveTab(rt.value)}>
            {rt.label}
          </Tab>
        ))}
      </div>

      {/* Loading state */}
      {listQuery.isLoading && (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spinner />
        </div>
      )}

      {/* Empty states */}
      {!listQuery.isLoading && totalForActiveTab === 0 && (
        <EmptyState
          activeTab={activeTab}
          search={search}
          isAdmin={isAdmin}
          onAdd={() => setAddOpen(true)}
          onClearSearch={() => {
            setSearchInput('');
            setSearch('');
          }}
        />
      )}

      {/* Meeting Recordings section */}
      {!listQuery.isLoading && sectionVisible('meetings') && (
        <MeetingRecordingsSection
          recordings={meetingRecordings}
          isAdmin={isAdmin}
          showExpired={showExpired}
          onToggleExpired={() => setShowExpired((s) => !s)}
        />
      )}

      {/* All other category sections */}
      {!listQuery.isLoading &&
        RESOURCE_TYPES.map((rt) => {
          const items = groupedByType.get(rt.value) ?? [];
          if (!sectionVisible(rt.value)) return null;
          if (items.length === 0 && activeTab === 'all') return null; // hide empty sections in All view
          return (
            <CategorySection
              key={rt.value}
              title={rt.label}
              items={items}
              isAdmin={isAdmin}
              onEdit={(r) => setEditing(r)}
            />
          );
        })}

      {/* Add modal */}
      {addOpen && (
        <AddEditModal
          mode="add"
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            showBanner('✔ Resource was successfully added.');
          }}
        />
      )}

      {/* Edit modal */}
      {editing && (
        <AddEditModal
          mode="edit"
          resource={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            showBanner('✔ Resource was successfully updated.');
          }}
          onDelete={() => setConfirmDelete(editing)}
        />
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <DeleteConfirmModal
          resource={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onDeleted={() => {
            setConfirmDelete(null);
            setEditing(null);
            showBanner('✔ Resource was successfully deleted.');
          }}
        />
      )}
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────────────

function Tab(props: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <div
      onClick={props.onClick}
      style={{
        padding: '6px 12px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        background: props.active ? '#1a3bcc' : 'transparent',
        color: props.active ? '#fff' : '#555',
      }}
    >
      {props.children}
    </div>
  );
}

function Spinner() {
  return (
    <div
      aria-label="Loading"
      style={{
        width: 32,
        height: 32,
        margin: '0 auto',
        border: '3px solid #ddd',
        borderTopColor: '#1a3bcc',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }}
    />
  );
}

function ConfirmBanner({ banner }: { banner: BannerState }) {
  if (!banner.visible) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '12px 24px',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 700,
        background: '#d4edda',
        color: '#1a7c3a',
        border: '1px solid #a3d4b0',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        zIndex: 99999,
      }}
    >
      {banner.text}
    </div>
  );
}

function EmptyState(props: {
  activeTab: 'all' | number | 'logos' | 'meetings';
  search: string;
  isAdmin: boolean;
  onAdd: () => void;
  onClearSearch: () => void;
}) {
  let message = 'The Resource Hub is empty. Resources will appear here as they\'re added.';
  let showClear = false;
  let showAdd = props.isAdmin;

  if (props.search) {
    message = 'No resources match your search.';
    showClear = true;
    showAdd = false;
  } else if (props.activeTab !== 'all' && props.activeTab !== 'logos') {
    message = 'No resources yet in this category.';
  }

  return (
    <div style={{ padding: '60px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#444', marginBottom: 8 }}>{message}</div>
      {showClear && (
        <button onClick={props.onClearSearch} style={linkBtnStyle}>
          Clear search
        </button>
      )}
      {showAdd && (
        <button onClick={props.onAdd} style={primaryBtnStyle}>
          + Add New BRG Resource
        </button>
      )}
    </div>
  );
}

function MeetingRecordingsSection(props: {
  recordings: HubResource[];
  isAdmin: boolean;
  showExpired: boolean;
  onToggleExpired: () => void;
}) {
  return (
    <Section
      title="Meeting Recordings"
      countLabel="All BRG Leaders Bimonthly Meetings · 2-year retention"
      rightSlot={
        props.isAdmin && (
          <label style={{ fontSize: 11, color: '#555' }}>
            <input type="checkbox" checked={props.showExpired} onChange={props.onToggleExpired} style={{ marginRight: 6 }} />
            Show expired recordings
          </label>
        )
      }
    >
      {props.recordings.map((r) => (
        <div key={r.hubResourceId} style={meetingRowStyle}>
          <div style={{ width: 40, height: 40, background: '#6a1b9a', borderRadius: 6, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>▶</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#111' }}>{r.title}</div>
            <div style={{ fontSize: 10, color: '#888' }}>
              {r.recordingDate && `Recorded ${formatDate(r.recordingDate)}`}
              {r.durationMinutes != null && ` · ${formatDuration(r.durationMinutes)}`}
              {r.host && ` · Hosted by ${r.host}`}
              {r.meetingType && ` · ${r.meetingType}`}
            </div>
          </div>
          {r.isExpired ? (
            <div style={{ fontSize: 10, color: '#a01a23', fontWeight: 600, background: '#f8d7da', padding: '2px 8px', borderRadius: 10 }}>Expired — file no longer available</div>
          ) : (
            r.expiresAt && (
              <div style={{ fontSize: 10, color: '#856404', fontWeight: 600, background: '#fff3cd', padding: '2px 8px', borderRadius: 10 }}>
                Expires {formatDate(r.expiresAt)}
              </div>
            )
          )}
        </div>
      ))}
    </Section>
  );
}

function CategorySection(props: { title: string; items: HubResource[]; isAdmin: boolean; onEdit: (r: HubResource) => void }) {
  if (props.items.length === 0) {
    return (
      <Section title={props.title} count={0}>
        <div style={{ padding: '30px 0', color: '#888', fontSize: 12 }}>No resources yet in this category.</div>
      </Section>
    );
  }

  // Determine if scrolling is needed (more than 8 items)
  const needsScroll = props.items.length > 8;
  // Max height accommodates ~1.75 rows of 4 columns (7 items visible without scrolling)
  // Ensures scroll bar appears on both desktop and mobile when there are more items
  const scrollContainerHeight = 350;

  return (
    <Section title={props.title} count={props.items.length}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12,
          ...(needsScroll && {
            maxHeight: scrollContainerHeight,
            overflowY: 'auto',
            paddingRight: 12,
            scrollBehavior: 'smooth',
          }),
        }}
        className={needsScroll ? 'scrollable-grid' : ''}
      >
        {props.items.map((r) => (
          <ResourceCard key={r.hubResourceId} resource={r} isAdmin={props.isAdmin} onEdit={() => props.onEdit(r)} />
        ))}
      </div>
    </Section>
  );
}

function ResourceCard({ resource, isAdmin, onEdit }: { resource: HubResource; isAdmin: boolean; onEdit: () => void }) {
  const isExternalLink = !!resource.externalLink;
  const downloadAvailable = resource.downloadAvailable;
  const [busy, setBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    setBusy(true);
    setDownloadError(null);
    try {
      const resp = await hubResourcesApi.getDownloadUrl(resource.hubResourceId);
      window.open(resp.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const e = err as { message?: string };
      setDownloadError(e.message ?? 'Download failed. Please retry.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={resourceCardStyle}>
      <div style={{ fontSize: 9, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
        {resource.resourceTypeLabel}
      </div>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#1a3bcc', lineHeight: 1.3, marginBottom: 8 }} title={resource.title}>
        {resource.title}
      </div>
      {resource.description && (
        <div style={{ fontSize: 11, color: '#555', lineHeight: 1.4, marginBottom: 10, minHeight: 32 }}>{resource.description}</div>
      )}
      <div style={{ fontSize: 10, color: '#999', marginBottom: 10 }}>Added: {formatDate(resource.createdAt)}</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button
          onClick={handleDownload}
          disabled={!downloadAvailable || busy}
          title={!downloadAvailable ? 'No file or link available' : undefined}
          style={{
            ...downloadBtnStyle,
            opacity: !downloadAvailable || busy ? 0.5 : 1,
            cursor: !downloadAvailable || busy ? 'not-allowed' : 'pointer',
          }}
        >
          {busy ? '…' : isExternalLink ? '↗ Visit' : '↓ Download'}
        </button>
        {isAdmin && (
          <a onClick={onEdit} style={editLinkStyle}>
            [edit]
          </a>
        )}
      </div>
      {downloadError && (
        <div style={{ marginTop: 6, fontSize: 10, color: '#c62828' }}>{downloadError}</div>
      )}
    </div>
  );
}

function Section(props: { title: string; count?: number; countLabel?: string; rightSlot?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <div style={{ fontSize: 15, fontWeight: 900, color: '#111' }}>{props.title}</div>
        <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>
          {props.countLabel ?? (props.count != null ? `${props.count} items` : '')}
        </div>
        {props.rightSlot}
      </div>
      {props.children}
    </div>
  );
}

function AddEditModal(props: {
  mode: 'add' | 'edit';
  resource?: HubResource;
  onClose: () => void;
  onSaved: () => void;
  onDelete?: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(props.resource?.title ?? '');
  const [description, setDescription] = useState(props.resource?.description ?? '');
  const [resourceType, setResourceType] = useState<number | ''>(props.resource?.resourceType ?? '');
  const [externalLink, setExternalLink] = useState(props.resource?.externalLink ?? '');
  const [error, setError] = useState<string | null>(null);

  // File-pick state — used by both Add (draft flow) and Edit (replace flow)
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [draftFileName, setDraftFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);

  // Existing file label for Edit mode
  const existingFileName = props.resource?.fileName ?? null;
  const isWebsiteType = resourceType === 3; // "Websites & Digital Hubs"
  const requiresFile = !isWebsiteType;

  // ─── Draft upload (Add mode, fires on file pick) ─────────────────────────
  const handleFilePick = async (file: File) => {
    setError(null);
    setFileError(null);

    const validationErr = validateFileForUpload(file);
    if (validationErr) {
      setFileError(validationErr);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);

    // Edit mode just holds the File; Replace happens at Save time.
    if (props.mode === 'edit') return;

    // Add mode: start the draft upload in the background.
    const controller = new AbortController();
    uploadAbortRef.current = controller;
    setUploadProgress(0);

    try {
      const draft = await hubResourcesApi.uploadDraft(
        file,
        (loaded, total) => setUploadProgress(total > 0 ? Math.round((loaded / total) * 100) : 0),
        controller.signal,
      );
      setDraftId(draft.draftId);
      setDraftFileName(draft.fileName);
      setUploadProgress(100);
    } catch (err) {
      const e = err as { message?: string };
      setFileError(e.message ?? 'Upload failed. Please retry.');
      setUploadProgress(null);
    } finally {
      uploadAbortRef.current = null;
    }
  };

  const handleRetryUpload = () => {
    if (selectedFile) void handleFilePick(selectedFile);
  };

  // ─── Cancel/close cleanup — delete any orphan draft ───────────────────────
  const handleClose = () => {
    if (uploadAbortRef.current) uploadAbortRef.current.abort();
    if (draftId) {
      // Fire-and-forget; backend is idempotent.
      void hubResourcesApi.deleteDraft(draftId).catch(() => {});
    }
    props.onClose();
  };

  useEffect(() => {
    // Cancel any in-flight upload if the modal is unmounted unexpectedly.
    return () => {
      if (uploadAbortRef.current) uploadAbortRef.current.abort();
    };
  }, []);

  // ─── Save ─────────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (props.mode === 'add') {
        const body: CreateHubResourceRequest = {
          title: title.trim(),
          description: description || null,
          resourceType: Number(resourceType),
          draftId: draftId ?? null,
          externalLink: isWebsiteType ? externalLink.trim() || null : null,
          fileName: draftFileName ?? selectedFile?.name ?? null,
        };
        return hubResourcesApi.create(body);
      }

      // Edit mode: metadata PUT, then optional file replace if user picked a new file
      const meta: UpdateHubResourceRequest = {
        title: title.trim(),
        description: description || null,
        resourceType: Number(resourceType),
        externalLink: isWebsiteType ? externalLink.trim() || null : undefined,
      };
      const updated = await hubResourcesApi.update(props.resource!.hubResourceId, meta);

      if (selectedFile && !isWebsiteType) {
        return hubResourcesApi.replaceFile(props.resource!.hubResourceId, selectedFile);
      }
      return updated;
    },
    onSuccess: () => {
      // Clear the local draftId so handleClose doesn't try to delete an attached draft
      setDraftId(null);
      void queryClient.invalidateQueries({ queryKey: ['hub-resources'] });
      props.onSaved();
    },
    onError: (err: unknown) => {
      const e = err as { message?: string };
      setError(e.message ?? 'Couldn\'t save. Try again.');
    },
  });

  // Save is enabled when: title + type filled, no validation errors, and
  // (Add: file uploaded OR URL provided)
  // (Edit: metadata only OR file picked successfully)
  const baseValid = title.trim().length > 0 && resourceType !== '' && !saveMutation.isPending;
  const addReady = props.mode === 'add'
    ? (isWebsiteType ? externalLink.trim().length > 0 : draftId !== null)
    : true;
  const uploadInFlight = uploadProgress !== null && uploadProgress < 100 && !fileError;
  const canSave = baseValid && addReady && !uploadInFlight;

  return (
    <>
      <div style={overlayStyle} onClick={handleClose} />
      <div style={modalStyle}>
        <div style={modalHeaderStyle}>
          <h2 style={{ fontSize: 14, fontWeight: 800 }}>{props.mode === 'add' ? 'Add New BRG Resource' : 'Edit Resource'}</h2>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', fontWeight: 700 }}>
            ✕
          </button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <FormRow label="Document Name *">
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={255} style={inputStyle} placeholder="Enter document or resource name" />
          </FormRow>
          <FormRow label="Resource Type *">
            <select value={resourceType} onChange={(e) => setResourceType(e.target.value ? Number(e.target.value) : '')} style={inputStyle}>
              <option value="">Select resource type</option>
              <option value={MEETING_RECORDING_TYPE}>Meeting Recordings</option>
              {RESOURCE_TYPES.map((rt) => (
                <option key={rt.value} value={rt.value}>
                  {rt.label}
                </option>
              ))}
            </select>
          </FormRow>
          <FormRow label="Document Description">
            <textarea value={description ?? ''} onChange={(e) => setDescription(e.target.value)} maxLength={2000} style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} placeholder="Brief description of the resource..." />
          </FormRow>

          {isWebsiteType ? (
            <FormRow label="Website URL *">
              <input
                type="url"
                value={externalLink}
                onChange={(e) => setExternalLink(e.target.value)}
                style={inputStyle}
                placeholder="https://example.com/page"
              />
            </FormRow>
          ) : requiresFile && (
            <FormRow label={props.mode === 'add' ? 'Choose File to Upload *' : 'Replace File (optional)'}>
              <div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="text"
                    value={draftFileName ?? selectedFile?.name ?? existingFileName ?? 'No file selected'}
                    readOnly
                    style={{ ...inputStyle, flex: 1, background: '#f4f4f4', color: '#555' }}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',')}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handleFilePick(f);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    style={secondaryBtnStyle}
                    disabled={uploadInFlight}
                  >
                    Browse
                  </button>
                </div>
                {uploadProgress !== null && uploadProgress < 100 && !fileError && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ background: '#e0e0e0', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ background: '#1a3bcc', height: '100%', width: `${uploadProgress}%`, transition: 'width 0.2s' }} />
                    </div>
                    <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>Uploading… {uploadProgress}%</div>
                  </div>
                )}
                {uploadProgress === 100 && !fileError && draftId && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#2e7d32' }}>✓ File ready</div>
                )}
                {fileError && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#c62828' }}>
                    {fileError}
                    {selectedFile && !uploadInFlight && (
                      <>
                        {' '}
                        <a onClick={handleRetryUpload} style={{ color: '#1a3bcc', cursor: 'pointer', textDecoration: 'underline' }}>
                          Retry
                        </a>
                      </>
                    )}
                  </div>
                )}
              </div>
            </FormRow>
          )}

          {error && <div style={{ color: '#c62828', fontSize: 11, marginTop: 8 }}>{error}</div>}
        </div>
        <div style={modalActionsStyle}>
          {props.mode === 'edit' && props.onDelete && (
            <button onClick={props.onDelete} style={{ ...secondaryBtnStyle, color: '#c62828', borderColor: '#c62828', marginRight: 'auto' }}>
              Delete
            </button>
          )}
          <button onClick={handleClose} style={secondaryBtnStyle}>
            Cancel
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!canSave}
            title={uploadInFlight ? 'Waiting for file upload to complete' : undefined}
            style={{ ...primaryBtnStyle, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }}
          >
            {props.mode === 'add' ? 'Save' : 'Update'}
          </button>
        </div>
      </div>
    </>
  );
}

function DeleteConfirmModal(props: { resource: HubResource; onCancel: () => void; onDeleted: () => void }) {
  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: () => hubResourcesApi.delete(props.resource.hubResourceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hub-resources'] });
      props.onDeleted();
    },
  });

  return (
    <>
      <div style={{ ...overlayStyle, zIndex: 10001 }} />
      <div style={{ ...modalStyle, width: 400, zIndex: 10002 }}>
        <div style={{ padding: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>Delete Resource</div>
          <div style={{ fontSize: 12, color: '#555', lineHeight: 1.5, marginBottom: 18 }}>
            Are you sure you want to delete this resource? This action cannot be undone.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={props.onCancel} style={secondaryBtnStyle}>
              Cancel
            </button>
            <button onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending} style={{ ...primaryBtnStyle, background: '#c62828' }}>
              Delete
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function FormRow(props: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#444', display: 'block', marginBottom: 4 }}>{props.label}</label>
      {props.children}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function formatDuration(minutes: number): string {
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m.toString().padStart(2, '0')}m`;
  }
  return `${minutes}m`;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const topControlsStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap', background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '12px 16px' };
const searchInputStyle: React.CSSProperties = { flex: 1, minWidth: 200, border: '1px solid #ccc', borderRadius: 4, padding: '7px 12px', fontSize: 12 };
const filterSelectStyle: React.CSSProperties = { border: '1px solid #ccc', borderRadius: 4, padding: '6px 10px', fontSize: 12, background: '#fff', cursor: 'pointer' };
const primaryBtnStyle: React.CSSProperties = { background: '#1a3bcc', color: '#fff', border: 'none', borderRadius: 4, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' };
const secondaryBtnStyle: React.CSSProperties = { background: '#fff', color: '#444', border: '1px solid #ccc', borderRadius: 4, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const tabsStyle: React.CSSProperties = { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18, padding: 4, background: '#fff', border: '1px solid #ddd', borderRadius: 6 };
const sectionStyle: React.CSSProperties = { background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '18px 20px', marginBottom: 16 };
const sectionHeaderStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 8, borderBottom: '2px solid #1a3bcc', gap: 12 };
const meetingRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', border: '1px solid #eee', borderRadius: 6, marginBottom: 8, background: '#fafbfc' };
const resourceCardStyle: React.CSSProperties = { border: '1px solid #ddd', borderRadius: 6, padding: 14, background: '#fafbfc' };
const downloadBtnStyle: React.CSSProperties = { background: '#fff', border: '1px solid #1a3bcc', color: '#1a3bcc', borderRadius: 4, padding: '5px 12px', fontSize: 11, fontWeight: 700, flex: 1, textAlign: 'center' };
const editLinkStyle: React.CSSProperties = { fontSize: 10, color: '#888', cursor: 'pointer', padding: '4px 6px' };
const overlayStyle: React.CSSProperties = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.45)', zIndex: 9999 };
const modalStyle: React.CSSProperties = { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: '#fff', borderRadius: 8, width: 480, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.25)', zIndex: 10000 };
const modalHeaderStyle: React.CSSProperties = { background: '#1a3bcc', color: '#fff', padding: '14px 18px', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const modalActionsStyle: React.CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 24px 20px', borderTop: '1px solid #eee' };
const inputStyle: React.CSSProperties = { width: '100%', border: '1px solid #ccc', borderRadius: 4, padding: '7px 10px', fontSize: 12, fontFamily: 'Arial, sans-serif' };
const linkBtnStyle: React.CSSProperties = { background: 'none', border: 'none', color: '#1a3bcc', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, fontWeight: 700, marginRight: 12 };
